/**
 * The painter's pen: the menus' colours, and text through per-colour glyph sheets (one drawImage a
 * character instead of a fillRect a pixel: the change list is over a thousand characters a frame).
 */

import core from '../../../content/palettes/core.json';
import { BUBBLE_GLYPH, CELL, drawText, FONT_CHARS, glyph, textWidth } from '../font8';
import type { HudColours } from '../hud/panels';
import { CX, FOOTER_Y, HEADING_Y, PLAY } from './layout';

/** The HUD's colour roles and three more the menus use. */
export interface MenuColours extends HudColours {
  /** The dark panel over the playfield, drawn translucent: core `ink.0`. */
  panel: string;
  /** The shadow under text over the title scene: core `ink.0`. */
  shadow: string;
  /** A value that warns (game speed below 100 %: PRACTICE): core `angry.2`. */
  warn: string;
}

/** Core `ink.0` (always present in the palette file). */
const INK = core.ramps.ink[0] ?? '';

/** Plain `HudColours` made whole: the missing roles from the core palette's ink, the warning as the accent. */
export function menuColours(c: HudColours | MenuColours): MenuColours {
  return 'panel' in c ? c : { ...c, panel: INK, shadow: INK, warn: c.accent };
}

const INDEX = new Map(FONT_CHARS.map((ch, i) => [ch, i]));
const sheets = new Map<string, HTMLCanvasElement | null>();

/** Every glyph in one colour, side by side in 8x8 cells. */
function sheet(colour: string): HTMLCanvasElement | null {
  const known = sheets.get(colour);
  if (known !== undefined) return known;
  let canvas: HTMLCanvasElement | null = null;
  if (typeof document !== 'undefined') {
    canvas = document.createElement('canvas');
    canvas.width = FONT_CHARS.length * CELL;
    canvas.height = CELL;
    const g = canvas.getContext('2d');
    if (g) {
      g.fillStyle = colour;
      FONT_CHARS.forEach((ch, i) => glyph(ch).forEach((row, r) => {
        for (let c = 0; c < row.length; c++) if (row[c] === '#') g.fillRect(i * CELL + c, r, 1, 1);
      }));
    } else {
      canvas = null;
    }
  }
  sheets.set(colour, canvas);
  return canvas;
}

/** `text` at (x, y), top-left, `scale` pixels per font pixel. */
export function put(
  ctx: CanvasRenderingContext2D, text: string, x: number, y: number, colour: string, scale = 1,
): void {
  const src = sheet(colour);
  if (!src) return drawText(ctx, text, x, y, colour, scale);
  const unknown = INDEX.get('?') ?? 0;
  const size = CELL * scale;
  let i = 0;
  for (const ch of text) {
    if (ch !== ' ') {
      const k = INDEX.get(ch.toUpperCase()) ?? unknown;
      ctx.drawImage(src, k * CELL, 0, CELL, CELL, Math.round(x + i * size), Math.round(y), size, size);
    }
    i++;
  }
}

/** Text with a one-pixel shadow down and right (over the title scene). */
export function shadowed(
  ctx: CanvasRenderingContext2D, text: string, x: number, y: number, colour: string, c: MenuColours, scale = 1,
): void {
  put(ctx, text, x + scale, y + scale, c.shadow, scale);
  put(ctx, text, x, y, colour, scale);
}

/** Text centred on x = `cx` (the playfield's middle by default). */
export function centred(
  ctx: CanvasRenderingContext2D, text: string, y: number, colour: string, scale = 1, cx = CX,
): void {
  put(ctx, text, Math.round(cx - textWidth(text, scale) / 2), y, colour, scale);
}

/** The cursor: a green glass bubble left of the chosen row, its glint in the glyph's notch. */
export function bubble(
  ctx: CanvasRenderingContext2D, x: number, y: number, c: MenuColours, scale = 1, shadow = false,
): void {
  if (shadow) put(ctx, BUBBLE_GLYPH, x + scale, y + scale, c.shadow, scale);
  put(ctx, BUBBLE_GLYPH, x, y, c.p1, scale);
  ctx.fillStyle = c.text;
  ctx.fillRect(x + 2 * scale, y + 2 * scale, 2 * scale, scale);
  ctx.fillRect(x + 2 * scale, y + 3 * scale, scale, scale);
}

/** The dark translucent panel over the playfield, and the screen's heading on it. */
export function cover(ctx: CanvasRenderingContext2D, heading: string, c: MenuColours): void {
  ctx.save();
  ctx.globalAlpha = 0.86;
  ctx.fillStyle = c.panel;
  ctx.fillRect(PLAY.x, PLAY.y, PLAY.w, PLAY.h);
  ctx.globalAlpha = 1;
  ctx.fillStyle = c.dim;
  ctx.fillRect(PLAY.x + 16, HEADING_Y + 20, PLAY.w - 32, 1);
  ctx.restore();
  centred(ctx, heading, HEADING_Y, c.p1, 2);
}

/** The key hints at the foot of a screen. */
export function footer(ctx: CanvasRenderingContext2D, text: string, c: MenuColours): void {
  centred(ctx, text, FOOTER_Y, c.dim);
}

/** A 10-segment bar for a 0..1 value, 60 px wide. */
export function bar(ctx: CanvasRenderingContext2D, v: number, x: number, y: number, on: string, off: string): void {
  const lit = Math.round(Math.min(1, Math.max(0, v)) * 10);
  for (let i = 0; i < 10; i++) {
    ctx.fillStyle = i < lit ? on : off;
    ctx.fillRect(x + i * 6, i < lit ? y : y + 5, 5, i < lit ? 7 : 2);
  }
}
